import { useEffect, useMemo, useRef, useState } from "react";
import { Container } from "react-bootstrap";
import PageLoading from "components/PageLoading";
import PaginationControls from "../../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls";
import SearchExport from "../../Documentation/Analysis/ComponentsAnalysis/SearchExport/SearchExport";
import HistoryPayment from "../HistoryPayment/HistoryPayment";
import FilterSectionComprovantesSindicato from "./FilterSectionComprovantesSindicato";
import ComprovantesSindicatoTable from "./ComprovantesSindicatoTable";
import {
  mapSolicitacoesToComprovantesSindicatoRows,
} from "./mapSolicitacoesToComprovantesSindicatoRows";
import {
  ALL_SITUATIONS_COMPROVANTES_SINDICATO,
  type ComprovantesSindicatoRow,
} from "./comprovantesSindicatoTypes";
import {
  onlyDigits,
  rowSituacaoComprovanteMatchesFilters,
} from "./comprovantesSindicatoUtils";
import {
  getDespesasExtratosAvaliacoesLegacy,
  getDespesasExtratosAvaliacoesSolicitacoes,
  getDespesasExtratosAvaliacaoEventos,
  getHistoricoAcaoComprovanteDespesa,
} from "../../../services/FinanceiroService";
import { getSindicatoByLoggedUser } from "../../../services/sindicatoService";
import { formatDateTimeBR } from "../../../utils/date";
import { getAnoRequestParam } from "../../Documentation/Extract/V2/anoRequestParam";
import {
  mapDespesaExtratoEventosToHistoryRows,
  type HistoryPaymentRow,
} from "../V2/mapDespesaExtratoEventosToHistoryRows";
import "../Payment.css";
import "../../Documentation/Analysis/ComponentsAnalysis/PaginationControls/PaginationControls.css";

const STORAGE_KEY = "comprovantes_sindicato_filters_v1";

const ALL_SITUATIONS = [...ALL_SITUATIONS_COMPROVANTES_SINDICATO];

type SortConfig = {
  key: string | null;
  direction: "asc" | "desc" | null;
};

type StoredFilters = {
  selectedSituation?: string[];
  searchTerm?: string;
  selectedYear?: number;
};

const readStoredFilters = (): StoredFilters => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const ComprovantesSindicato = () => {
  const stored = readStoredFilters();
  const initialSituations = (() => {
    if (!Array.isArray(stored.selectedSituation)) return ALL_SITUATIONS;
    const allowed = new Set<string>(ALL_SITUATIONS);
    const sanitized = stored.selectedSituation.filter(
      (s: unknown): s is string => typeof s === "string" && allowed.has(s),
    );
    const newlyAdded = ALL_SITUATIONS.filter((s) => !sanitized.includes(s));
    const result = [...sanitized, ...newlyAdded];
    return result.length > 0 ? result : ALL_SITUATIONS;
  })();

  const [data, setData] = useState<ComprovantesSindicatoRow[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([2026, 2025]);
  const [selectedYear, setSelectedYear] = useState<number>(
    typeof stored.selectedYear === "number" ? stored.selectedYear : 2026,
  );
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: null,
    direction: null,
  });
  const [selectedSituation, setSelectedSituation] =
    useState<string[]>(initialSituations);
  const [searchTerm, setSearchTerm] = useState(
    typeof stored.searchTerm === "string" ? stored.searchTerm : "",
  );
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [historySindicatoId, setHistorySindicatoId] = useState<number | null>(
    null,
  );
  const [historyDataExtract, setHistoryDataExtract] = useState<
    HistoryPaymentRow[]
  >([]);
  const skipNextYearFetchRef = useRef(false);

  const extractAnosModelo = (
    response:
      | {
          avaliacoesModelo?: { anoReferencia: number }[];
        }
      | null
      | undefined,
  ): number[] => {
    const anos =
      response?.avaliacoesModelo?.map((m) => m.anoReferencia) ?? [];
    return [...new Set(anos)].sort((a, b) => b - a);
  };

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ selectedSituation, searchTerm, selectedYear }),
      );
    } catch {
      /* ignore */
    }
  }, [selectedSituation, searchTerm, selectedYear]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const loadRowsForYear = async (ano: number) => {
    // V1 (2025): GET .../legacy — não escopado pelo token, precisa filtrar pelo CNPJ do sindicato logado
    if (ano === 2025) {
      let cnpjFiltro: string | null = null;
      try {
        const sindicatoResp = await getSindicatoByLoggedUser();
        const sindicato = sindicatoResp?.data ?? sindicatoResp;
        cnpjFiltro = (sindicato as { cnpj?: string })?.cnpj ?? null;
      } catch {

      }

      const response = await getDespesasExtratosAvaliacoesLegacy();
      return mapSolicitacoesToComprovantesSindicatoRows(
        response?.comprovantes ?? [],
        ano,
        cnpjFiltro,
      );
    }

    // V2 (2026+): GET .../solicitacoes?anoReferencia=
    const response = await getDespesasExtratosAvaliacoesSolicitacoes(
      getAnoRequestParam(ano),
    );
    const comprovantes = response?.comprovantes ?? [];
    const anosModelo = extractAnosModelo(response).filter((y) => y !== 2025);
    if (anosModelo.length > 0) {
      setAvailableYears(anosModelo.includes(2025) ? anosModelo : [...anosModelo, 2025].sort((a, b) => b - a));
    }

    return mapSolicitacoesToComprovantesSindicatoRows(comprovantes, ano);
  };

  useEffect(() => {
    const fetchRows = async () => {
      setLoading(true);
      try {
        const rows = await loadRowsForYear(selectedYear);
        setData(rows);
      } catch (error) {
        console.error("Erro ao carregar comprovantes do sindicato:", error);
        setData([]);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    if (skipNextYearFetchRef.current) {
      skipNextYearFetchRef.current = false;
      return;
    }

    fetchRows();
  }, [selectedYear]);

  const handleSituationFilter = (value: string | string[]) => {
    if (Array.isArray(value)) {
      setSelectedSituation(value);
      setCurrentPage(1);
      return;
    }
    setSelectedSituation((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value],
    );
    setCurrentPage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        const direction =
          prev.direction === "asc"
            ? "desc"
            : prev.direction === "desc"
              ? null
              : "asc";
        return { key: direction ? key : null, direction };
      }
      return { key, direction: "asc" };
    });
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key) return "↕";
    return sortConfig.direction === "asc" ? "↑" : "↓";
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const termDigits = onlyDigits(searchTerm);

    return data.filter((row) => {
      if (
        !rowSituacaoComprovanteMatchesFilters(
          row.situacao,
          selectedSituation,
          ALL_SITUATIONS,
        )
      ) {
        return false;
      }

      if (!term) return true;

      const solicitacao = String(row.solicitacao || "").toLowerCase();
      const sigla = String(row.sigla || "").toLowerCase();
      const cnpj = onlyDigits(row.cnpj);

      return (
        solicitacao.includes(term) ||
        sigla.includes(term) ||
        (termDigits.length > 0 && cnpj.includes(termDigits))
      );
    });
  }, [data, searchTerm, selectedSituation]);

  const sortedData = useMemo(() => {
    if (!sortConfig.key || !sortConfig.direction) return filteredData;

    const key = sortConfig.key;
    const dir = sortConfig.direction === "asc" ? 1 : -1;
    const dateKeys = new Set([
      "envio",
      "solicitacaoCorrecao",
      "correcao",
      "envioFormulario",
    ]);

    return [...filteredData].sort((a, b) => {
      const av = (a as Record<string, unknown>)[key];
      const bv = (b as Record<string, unknown>)[key];

      if (dateKeys.has(key)) {
        const at = av ? new Date(String(av)).getTime() : 0;
        const bt = bv ? new Date(String(bv)).getTime() : 0;
        return (at - bt) * dir;
      }

      return String(av ?? "").localeCompare(String(bv ?? ""), "pt-BR") * dir;
    });
  }, [filteredData, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / itemsPerPage));
  const startResult =
    sortedData.length === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endResult = Math.min(currentPage * itemsPerPage, sortedData.length);
  const currentItems = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const exportData = useMemo(
    () =>
      sortedData.map((item) => ({
        SOLICITACAO: item.solicitacao ?? "",
        SITUACAO: item.situacao ?? "",
        ENVIO_COMPROVANTES: formatDateTimeBR(item.envio),
        SOLICITACAO_CORRECAO: formatDateTimeBR(item.solicitacaoCorrecao),
        CORRECAO_COMPROVANTES: formatDateTimeBR(item.correcao),
        ENVIO_FORMULARIO: formatDateTimeBR(item.envioFormulario),
      })),
    [sortedData],
  );

  const handleOpenHistory = async (item: ComprovantesSindicatoRow) => {
    const extratoId = item.extratoId ?? item.solicitacaoId ?? null;
    const isV2 = selectedYear > 2025 && extratoId != null;
    const historicoId = item.idSindicato ?? item.id;
    setHistorySindicatoId(historicoId);

    try {
      if (isV2) {
        const eventos = await getDespesasExtratosAvaliacaoEventos(extratoId);
        setHistoryDataExtract(mapDespesaExtratoEventosToHistoryRows(eventos));
        setShowHistoryModal(true);
        return;
      }

      const resp = await getHistoricoAcaoComprovanteDespesa(historicoId);
      const lista = Array.isArray(resp) ? resp : [];

      const formatted: HistoryPaymentRow[] = lista.map(
        (
          entry: {
            acao?: string;
            titulo?: string;
            data?: string;
            dataHora?: string;
            id?: number;
          },
          i: number,
        ) => ({
          acao: entry.acao ?? entry.titulo ?? "-",
          data: entry.data ?? entry.dataHora ?? null,
          id: entry.id ?? i,
          arquivoId: null,
        }),
      );

      setHistoryDataExtract(formatted);
      setShowHistoryModal(true);
    } catch (error) {
      console.error("Erro ao carregar histórico de comprovantes:", error);
      setHistoryDataExtract([]);
      setShowHistoryModal(true);
    }
  };

  if (initialLoading || loading) {
    return <PageLoading />;
  }

  return (
    <Container fluid className="payment-container lista-sindicatos-page">
      <h1 className="payment-title">LISTA DE COMPROVANTES</h1>

      <div className="filters-actions-container">
        <div className="filters-search-row">
          <FilterSectionComprovantesSindicato
            selectedSituation={selectedSituation}
            handleSituationFilter={handleSituationFilter}
            allSituations={ALL_SITUATIONS}
          />

          <SearchExport
            selectedYear={selectedYear}
            setSelectedYear={setSelectedYear}
            availableYears={availableYears}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            exportData={exportData}
            fileName={`ListaComprovantesSindicato_${selectedYear}`}
          />
        </div>

        <ComprovantesSindicatoTable
          currentItems={currentItems}
          handleSort={handleSort}
          getSortIcon={getSortIcon}
          selectedYear={selectedYear}
          onOpenHistory={handleOpenHistory}
          formatDate={formatDateTimeBR}
        />
      </div>

      <div className="pagination-wrapper">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          setCurrentPage={setCurrentPage}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          startResult={startResult}
          endResult={endResult}
          totalItems={sortedData.length}
        />

        <HistoryPayment
          show={showHistoryModal}
          onHide={() => setShowHistoryModal(false)}
          idSindicato={historySindicatoId}
          historyDataExtract={historyDataExtract}
        />
      </div>
    </Container>
  );
};

export default ComprovantesSindicato;
